import React from 'react';
import { AlertTriangle, Download, Globe } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface BrowserNotSupportedOverlayProps {
  browserName: string;
  browserVersion: string;
  isVisible: boolean;
}

export function BrowserNotSupportedOverlay({
  browserName,
  browserVersion,
  isVisible
}: BrowserNotSupportedOverlayProps) {
  if (!isVisible) return null;

  const supportedBrowsers = [
    {
      name: 'Google Chrome',
      icon: '🌐',
      downloadUrl: 'https://www.google.com/chrome/',
      description: 'Fast, secure, and feature-rich browser'
    },
    {
      name: 'Brave Browser',
      icon: '🦁',
      downloadUrl: 'https://brave.com/',
      description: 'Privacy-first browser with built-in ad blocking'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="max-w-2xl w-full my-8">
        <div className="bg-[#1E293B] border border-slate-700 rounded-xl p-8 shadow-2xl">
          {/* Icon and Alert */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-slate-700 rounded-full flex items-center justify-center">
                <Globe className="h-10 w-10 text-gray-400"/>
              </div>
              <div className="absolute -top-2 -right-2 bg-red-500 rounded-full p-2">
                <AlertTriangle className="h-5 w-5 text-white"/>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="text-center space-y-3 mb-6">
            <h1 className="text-3xl font-bold text-white">
              Browser Not Supported
            </h1>
            <p className="text-lg text-gray-400">
              You're currently using <span className="text-red-400 font-medium">{browserName} {browserVersion}</span>
            </p>
          </div>

          {/* Description */}
          <Card className="p-4 bg-slate-800/50 border border-slate-600 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0"/>
              <div>
                <h3 className="font-semibold text-white mb-2">Why This Browser Isn't Supported</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Our video chat platform relies on advanced WebRTC features and modern web APIs that work best in
                  Chrome and Brave browsers. These browsers provide the most reliable video calling experience with
                  optimal performance and security.
                </p>
              </div>
            </div>
          </Card>

          {/* Supported Browsers */}
          <div className="space-y-4 mb-6">
            <h2 className="text-xl font-semibold text-white text-center">
              Please use one of these supported browsers:
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {supportedBrowsers.map((browser) => (
                <Card
                  key={browser.name}
                  className="p-4 bg-slate-800/50 border border-slate-600 hover:border-slate-500 transition-colors"
                >
                  <div className="text-center space-y-3">
                    <div className="text-3xl">{browser.icon}</div>
                    <h3 className="font-medium text-white">{browser.name}</h3>
                    <p className="text-xs text-gray-400">{browser.description}</p>
                    <Button
                      asChild
                      className="w-full bg-[#11B981] hover:bg-[#0EA574] text-white"
                      size="sm"
                    >
                      <a
                        href={browser.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2"
                      >
                        <Download className="h-4 w-4"/>
                        Download
                      </a>
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Instructions */}
          <Card className="p-4 bg-slate-800/50 border border-slate-600">
            <h3 className="font-semibold text-white text-center mb-3">After Installing a Supported Browser:</h3>
            <ol className="text-sm text-gray-300 space-y-2">
              <li className="flex items-start">
                <span className="inline-block w-6 h-6 bg-[#11B981] text-white rounded-full text-xs font-bold flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  1
                </span>
                Download and install one of the supported browsers above
              </li>
              <li className="flex items-start">
                <span className="inline-block w-6 h-6 bg-[#11B981] text-white rounded-full text-xs font-bold flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  2
                </span>
                Close this browser and open the video chat app in your new browser
              </li>
              <li className="flex items-start">
                <span className="inline-block w-6 h-6 bg-[#11B981] text-white rounded-full text-xs font-bold flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  3
                </span>
                Allow camera and microphone permissions when prompted
              </li>
              <li className="flex items-start">
                <span className="inline-block w-6 h-6 bg-[#11B981] text-white rounded-full text-xs font-bold flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  4
                </span>
                Start connecting with people around the world!
              </li>
            </ol>
          </Card>

          {/* Footer */}
          <p className="text-xs text-gray-500 text-center mt-4">
            For the best video calling experience, we recommend using the latest version of your chosen browser.
          </p>
        </div>
      </div>
    </div>
  );
}
